import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import {
  fetchResidentProfile,
  patchResidentProfile,
} from "../../services/resident_api";
import CommonPopup from "../../components/common/CommonPopup";
import { validateEmail, validateRequired } from "../../utils/validation";
import SubHeader from "../../components/Header/SubmenuHeader";

const ManageAccount = () => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const [showPopup, setShowPopup] = useState(false);
  const { user, login } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    title: "Mr.",
    firstName: "",
    lastName: "",
    phoneCode: "+91",
    phoneNumber: "",
    email: "",
    flatNo: "",
    wing: "",
    building: "",
    street: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
  });

  const [initialFormData, setInitialFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });

    fetchProfile();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetchResidentProfile();

      if (response) {
        const flat = Array.isArray(response.flats)
          ? response.flats[0] || {}
          : {};

        const newFormData = {
          title: "Mr.",
          firstName: response.name?.split(" ")[0] || "",
          lastName: response.name?.split(" ").slice(1).join(" ") || "",
          phoneCode: "+91",
          phoneNumber: response.phone_number || "",
          email: response.email_id || "",
          flatNo: flat.flat_no || "",
          wing: flat.wing_name || "",
          building: flat.building_name || "",
          street: "",
          city: flat.city || flat.location || "",
          state: flat.state || "",
          country: flat.country || "",
          zipCode: flat.pincode || "",
        };

        setFormData(newFormData);
        setInitialFormData(newFormData);
      }
    } catch (err) {
      console.error("Profile fetch failed:", err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Check if first name, last name, or email has changed
  useEffect(() => {
    const hasFirstNameChanged =
      formData.firstName !== initialFormData.firstName;
    const hasLastNameChanged = formData.lastName !== initialFormData.lastName;
    const hasEmailChanged = formData.email !== initialFormData.email;

    setIsSaveEnabled(
      hasFirstNameChanged || hasLastNameChanged || hasEmailChanged
    );
  }, [formData, initialFormData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submission if button is disabled
    if (!isSaveEnabled) return;

    const newErrors = {};
    const requiredError = validateRequired(formData.email, "Email");
    if (requiredError) {
      newErrors.email = requiredError;
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`,
        phone_number: formData.phoneNumber,
        email_id: formData.email,
      };

      const result = await patchResidentProfile(payload);
      if (result) {
        const updatedUser = {
          ...user,
          name: payload.name,
          email_id: payload.email_id,
        };
        const tokens = JSON.parse(localStorage.getItem("authTokens"));

        // Keep the same user_type from existing context
        const user_type = user?.user_type || "resident"; // fallback if missing

        // Update the context and localStorage
        login(tokens, updatedUser, user_type);

        setShowPopup(true);
        // Update initial form data after successful save
        setInitialFormData(formData);
        setIsSaveEnabled(false);
      }
    } catch (err) {
      console.error("Update failed:", err);
      alert("❌ Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* Header */}
      <SubHeader title={"Manage Your Account"} />

      {/* Main Content - This will push footer to bottom */}
      <div className="flex-1 w-full">
        <div className="max-w-xl mx-auto px-4 py-6">
          {/* Avatar */}
          <div className="flex justify-center sm:justify-start mb-6">
            <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg flex items-center justify-center bg-blue-600 text-white text-3xl font-bold">
              {formData.firstName
                ? `${formData.firstName[0]}`.toUpperCase()
                : "U"}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-600 animate-pulse">
              Loading profile...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Section */}
              <div className="flex flex-wrap gap-2">
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-20 p-2 border rounded-lg bg-white"
                >
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Dr.">Dr.</option>
                </select>
                <input
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(e) => {
                    const textOnlyValue = e.target.value.replace(
                      /[^A-Za-z\s']/g,
                      ""
                    );
                    handleChange({
                      target: { name: e.target.name, value: textOnlyValue },
                    });
                  }}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg"
                />
                <input
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(e) => {
                    const textOnlyValue = e.target.value.replace(
                      /[^A-Za-z\s']/g,
                      ""
                    );
                    handleChange({
                      target: { name: e.target.name, value: textOnlyValue },
                    });
                  }}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg"
                />
              </div>

              {/* Phone Number */}
              <div className="flex w-full">
                <span className="w-16 flex items-center justify-center px-2 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-l-lg">
                  +91
                </span>
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="flex-1 p-2 border border-l-0 border-gray-300 rounded-r-lg"
                  disabled
                />
              </div>

              {/* Email */}
              <div>
                <input
                  name="email"
                  placeholder="Email ID"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Building */}
              <input
                name="building"
                placeholder="Building / Apartment Name"
                value={formData.building}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg bg-gray-100"
                disabled
              />

              {/* Flat No. and Wing */}
              <div className="flex flex-wrap gap-2">
                <input
                  name="flatNo"
                  placeholder="Flat No."
                  value={formData.flatNo}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg"
                  disabled
                />
                <input
                  name="wing"
                  placeholder="Wing"
                  value={formData.wing}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg"
                  disabled
                />
              </div>

              {/* Street */}
              <input
                name="street"
                placeholder="Street Name OR Number"
                value={formData.street}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg bg-gray-100"
                disabled
              />

              {/* Country and Zip Code */}
              <div className="flex flex-wrap gap-2">
                <input
                  name="country"
                  placeholder="Country"
                  value={formData.country}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg bg-gray-100"
                  disabled
                />
                <input
                  name="zipCode"
                  placeholder="Zip Code"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg bg-gray-100"
                  disabled
                />
              </div>

              {/* City and State */}
              <div className="flex flex-wrap gap-2">
                <input
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg bg-gray-100"
                  disabled
                />
                <input
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                  className="flex-1 min-w-[140px] p-2 border rounded-lg bg-gray-100"
                  disabled
                />
              </div>

              {/* Save Button - Always Visible but Conditionally Disabled */}
              <button
                type="submit"
                disabled={!isSaveEnabled}
                className={`w-full py-3 rounded-lg font-semibold transition mt-6 ${
                  isSaveEnabled
                    ? "bg-[#004990] text-white hover:bg-[#003570] cursor-pointer"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                SAVE
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Popup */}
      {showPopup && (
        <CommonPopup
          isOpen={showPopup}
          icon={"✅ "}
          title="Profile updated successfully!"
          message="Updated successfully."
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default ManageAccount;
